// Local-only encrypted store. Private financial inputs live ONLY here, in the
// browser, encrypted at rest with a passphrase via the Web Crypto API
// (PBKDF2 -> AES-GCM). Nothing in this file ever makes a network request.

// Per-user encrypted vaults. Each profile is a separate ciphertext blob keyed
// by username; a username registry lets us show a picker. Everything is still
// encrypted client-side with the user's PIN/passphrase and never leaves the
// browser.
const USERS_KEY = "afm.users";
const LEGACY_KEY = "afm.vault.v1";
const vaultKey = (username: string) => `afm.vault.u.${username}`;
const PBKDF2_ITERS = 250_000;

interface VaultBlob {
  v: 1;
  salt: string; // base64
  iv: string; // base64
  data: string; // base64 ciphertext
}

function b64encode(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function b64decode(s: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
}

async function deriveKey(passphrase: string, salt: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: PBKDF2_ITERS, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

export class Vault {
  private key: CryptoKey | null = null;

  private username: string | null = null;

  /** List existing profile usernames. */
  static listUsers(): string[] {
    try {
      const arr = JSON.parse(localStorage.getItem(USERS_KEY) ?? "[]");
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  }

  static exists(username: string): boolean {
    return localStorage.getItem(vaultKey(username)) !== null;
  }

  /** Migrate a pre-multi-user single vault into a named profile ("me"). */
  static migrateLegacy(): void {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy && Vault.listUsers().length === 0) {
      localStorage.setItem(vaultKey("me"), legacy);
      localStorage.setItem(USERS_KEY, JSON.stringify(["me"]));
      localStorage.removeItem(LEGACY_KEY);
    }
  }

  get isUnlocked(): boolean {
    return this.key !== null;
  }
  get user(): string | null {
    return this.username;
  }

  /** Create a brand-new profile protected by `pin`. */
  async initialize<T>(username: string, pin: string, initial: T): Promise<void> {
    if (Vault.exists(username)) throw new Error("That profile name is taken");
    const salt = crypto.getRandomValues(new Uint8Array(16));
    this.key = await deriveKey(pin, salt);
    this.username = username;
    await this.write(initial, salt);
    const users = Vault.listUsers();
    if (!users.includes(username)) {
      users.push(username);
      localStorage.setItem(USERS_KEY, JSON.stringify(users));
    }
  }

  /** Unlock a profile. Returns decrypted data, or throws on wrong PIN. */
  async unlock<T>(username: string, pin: string): Promise<T> {
    const raw = localStorage.getItem(vaultKey(username));
    if (!raw) throw new Error("No such profile");
    const blob = JSON.parse(raw) as VaultBlob;
    const salt = b64decode(blob.salt);
    const key = await deriveKey(pin, salt);
    const iv = b64decode(blob.iv);
    try {
      const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, b64decode(blob.data));
      this.key = key;
      this.username = username;
      return JSON.parse(new TextDecoder().decode(plain)) as T;
    } catch {
      throw new Error("Incorrect PIN");
    }
  }

  /** Persist data (re-encrypting with the unlocked key). */
  async save<T>(data: T): Promise<void> {
    if (!this.key || !this.username) throw new Error("Vault is locked");
    const existing = localStorage.getItem(vaultKey(this.username));
    const salt = existing ? b64decode((JSON.parse(existing) as VaultBlob).salt) : null;
    await this.write(data, salt);
  }

  private async write<T>(data: T, saltIn: Uint8Array | null): Promise<void> {
    if (!this.key || !this.username) throw new Error("Vault is locked");
    const salt = saltIn ?? crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const plaintext = new TextEncoder().encode(JSON.stringify(data));
    const cipher = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, this.key, plaintext);
    const blob: VaultBlob = {
      v: 1,
      salt: b64encode(salt.buffer as ArrayBuffer),
      iv: b64encode(iv.buffer as ArrayBuffer),
      data: b64encode(cipher),
    };
    localStorage.setItem(vaultKey(this.username), JSON.stringify(blob));
  }

  lock(): void {
    this.key = null;
    this.username = null;
  }

  /** Irreversibly delete a profile. */
  static destroy(username: string): void {
    localStorage.removeItem(vaultKey(username));
    const users = Vault.listUsers().filter((u) => u !== username);
    localStorage.setItem(USERS_KEY, JSON.stringify(users));
  }
}
